import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { onboardingAPI, InitialRegisterPayload } from './services/onboardingService';
import './design-system/styles/pages/initial-register.css';
import './design-system/styles/pages/landing-page.css';

const InitialRegister: React.FC = () => {
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [rolePassword, setRolePassword] = useState<string>('');
    const [confirmRolePassword, setConfirmRolePassword] = useState<string>('');
    const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [scrollY, setScrollY] = useState<number>(0);
    const navigate = useNavigate();
    const { user, initializing } = useAuth();

    // Redirect if user is already logged in (but wait for auth to initialize)
    useEffect(() => {
        if (!initializing && user) {
            navigate('/', { replace: true });
        }
    }, [user, initializing, navigate]);

    // Scroll handler for header
    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = (): void => {
        setIsMenuOpen(!isMenuOpen);
    };

    // Telefon numarası normalleştirme
    const normalizePhone = (phone: string | null | undefined): string => {
        if (!phone) return '';
        return String(phone).replace(/\D/g, '');
    };

    // Telefon numarasını formatla (0(XXX) XXX XX XX formatında göster)
    const formatPhone = (phone: string | null | undefined): string => {
        if (!phone) return '';
        let cleaned = normalizePhone(phone);
        if (cleaned.length === 0) return '';
        
        // Eğer 0 ile başlamıyorsa ve 10 rakam varsa başına 0 ekle
        if (cleaned.length === 10 && cleaned[0] !== '0') {
            cleaned = '0' + cleaned;
        }
        
        // Türkiye telefon formatı: 0(XXX) XXX XX XX (11 rakam: 0 + 10 rakam)
        if (cleaned.length <= 1) {
            return cleaned;
        } else if (cleaned.length <= 4) {
            // 0(XXX
            return `${cleaned.slice(0, 1)}(${cleaned.slice(1)}`;
        } else if (cleaned.length <= 7) {
            // 0(XXX) XXX
            return `${cleaned.slice(0, 1)}(${cleaned.slice(1, 4)}) ${cleaned.slice(4)}`;
        } else if (cleaned.length <= 9) {
            // 0(XXX) XXX XX
            return `${cleaned.slice(0, 1)}(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
        } else {
            // 11 rakam: 0(XXX) XXX XX XX
            return `${cleaned.slice(0, 1)}(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)} ${cleaned.slice(7, 9)} ${cleaned.slice(9, 11)}`;
        }
    };

    // Türkçe duyarlı büyük harfe çevir
    const toTurkishUpperCase = (text: string): string => {
        if (!text) return '';
        return text
            .replace(/i/g, 'İ')
            .replace(/ı/g, 'I')
            .replace(/ş/g, 'Ş')
            .replace(/ğ/g, 'Ğ')
            .replace(/ü/g, 'Ü')
            .replace(/ö/g, 'Ö')
            .replace(/ç/g, 'Ç')
            .toUpperCase();
    };

    // Telefon input'u için özel handler
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Sadece rakam kabul et (boşlukları ve format karakterlerini kaldır)
        const digitsOnly = value.replace(/\D/g, '');
        // Maksimum 11 rakam (0 + 10 rakam)
        const maxDigits = 11;
        let limited = digitsOnly.slice(0, maxDigits);
        
        // Eğer 10 rakam girildiyse ve 0 ile başlamıyorsa, 0 ekle
        if (limited.length === 10 && limited[0] !== '0') {
            limited = '0' + limited;
        }
        
        // phone state'inde sadece rakamları sakla (formatPhone input'ta gösterilecek)
        setPhone(limited);
    };

    // Ad için handler - otomatik büyük harf
    const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFirstName(toTurkishUpperCase(value));
    };

    // Soyad için handler - otomatik büyük harf
    const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLastName(toTurkishUpperCase(value));
    };

    const buildRegistrationPayload = (): InitialRegisterPayload => ({
        owner: {
            firstName,
            lastName,
            phone: normalizePhone(phone), // Normalize edilmiş telefon numarasını gönder
            email: email.toLowerCase(),
            password,
            rolePassword
        },
        company: {
            name: `${firstName} ${lastName}`,
            phone: normalizePhone(phone), // Normalize edilmiş telefon numarasını gönder
            email: email.toLowerCase()
        },
        termsAccepted
    });

    const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        if (password !== confirmPassword) { setError('Giriş şifreleri eşleşmiyor!'); setIsLoading(false); return; }
        if (rolePassword !== confirmRolePassword) { setError('Admin şifreleri eşleşmiyor!'); setIsLoading(false); return; }
        if (password.length < 6) { setError('Giriş şifresi en az 6 karakter olmalıdır!'); setIsLoading(false); return; }
        if (rolePassword.length < 6) { setError('Admin şifresi en az 6 karakter olmalıdır!'); setIsLoading(false); return; }
        if (!termsAccepted) { setError('Hizmet sözleşmesini kabul etmelisiniz!'); setIsLoading(false); return; }
        
        // Telefon numarası validasyonu (normalize edilmiş hali ile)
        const normalizedPhone = normalizePhone(phone);
        const phoneRegex = /^(\+90|0)?[5][0-9]{9}$/;
        if (!normalizedPhone || !phoneRegex.test(normalizedPhone)) {
            setError('Geçerli bir telefon numarası girin (örn: 05551234567).');
            setIsLoading(false);
            return;
        }
        
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Geçerli bir e-posta adresi girin.'); setIsLoading(false); return; }

        try {
            const payload = buildRegistrationPayload();
            const response = await onboardingAPI.registerCompany(payload);
            
            const success = response.data?.success ?? true;

            if (!success) {
                setError(response.data?.message || 'Kayıt işlemi başarısız oldu.');
                setIsLoading(false);
                return;
            }

            // Redirect to SMS verification page
            const responseData = response.data?.data;
            
            if (responseData && responseData.ownerId) {
                navigate('/verify-sms', {
                    state: {
                        userId: responseData.ownerId,
                        phoneNumber: normalizePhone(phone), // Normalize edilmiş telefon numarasını gönder
                        companyId: responseData.companyId
                    }
                });
            } else {
                setError('Kayıt başarılı ancak doğrulama sayfasına yönlendirilemedi.');
                setIsLoading(false);
            }
        } catch (apiError: any) {
            const backendMessage = apiError?.response?.data?.message;
            setError(backendMessage || 'Kayıt işlemi sırasında beklenmeyen bir hata oluştu.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="initial-register-page">
            {/* Header */}
            <header className={`landing-header ${scrollY > 50 ? 'scrolled' : ''}`}>
                <nav className="landing-nav">
                    <a href="/" className="landing-logo">
                        <div className="logo-icon">🔧</div>
                        <span className="logo-text">
                            <span className="logo-text-primary">T</span>
                            <span className="logo-text-secondary">servis</span>
                        </span>
                    </a>

                    <ul className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
                        <li><a href="/#features" onClick={(e) => { e.preventDefault(); navigate('/#features'); }}>Özellikler</a></li>
                        <li><a href="/#pricing" onClick={(e) => { e.preventDefault(); navigate('/#pricing'); }}>Fiyatlar</a></li>
                        <li><a href="/signin" onClick={(e) => { e.preventDefault(); navigate('/signin'); }}>Giriş Yap</a></li>
                        <li><a href="/register" onClick={(e) => { e.preventDefault(); navigate('/register'); }} className="nav-cta">14 Gün Ücretsiz Deneyin</a></li>
                    </ul>

                    <button
                        className={`mobile-menu-btn ${isMenuOpen ? 'active' : ''}`}
                        onClick={toggleMenu}
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>
                </nav>
            </header>

            {/* Initial Register Form */}
            <div className="initial-register-container">
                <div className="initial-form-header">
                    <h2 className="initial-form-title">Şirket ve Admin Bilgileri</h2>
                    <p className="initial-form-subtitle">Şirketiniz için gerekli tüm bilgileri girin</p>
                        </div>

                <form onSubmit={handleRegister}>
                    {/* Firma Sahibi Bilgileri */}
                    <div className="initial-form-row">
                        <div className="initial-form-group">
                            <label className="initial-form-label">Firma Sahibi Adı</label>
                                    <input aria-label="text input"
                                        type="text"
                                className="initial-form-input"
                                        value={firstName}
                                        onChange={handleFirstNameChange}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                        <div className="initial-form-group">
                            <label className="initial-form-label">Firma Sahibi Soyadı</label>
                                    <input aria-label="text input"
                                        type="text"
                                className="initial-form-input"
                                        value={lastName}
                                        onChange={handleLastNameChange}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>

                    <div className="initial-form-row">
                        <div className="initial-form-group">
                            <label className="initial-form-label">Telefon</label>
                                <input aria-label="tel input"
                                    type="tel"
                                className="initial-form-input"
                                    value={formatPhone(phone)}
                                    onChange={handlePhoneChange}
                                    required
                                    disabled={isLoading}
                                    placeholder="0(555) 123 45 67"
                                />
                            </div>
                        <div className="initial-form-group">
                            <label className="initial-form-label">E-posta</label>
                                <input aria-label="email input"
                                    type="email"
                                className="initial-form-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                                    required
                                    disabled={isLoading}
                                />
                        </div>
                            </div>

                    <div className="initial-form-row">
                        <div className="initial-form-group">
                            <label className="initial-form-label">Giriş Şifresi</label>
                            <input aria-label="password input"
                                type="password"
                                className="initial-form-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={isLoading}
                                placeholder="Sisteme giriş için kullanılacak şifre"
                            />
                        </div>
                        <div className="initial-form-group">
                            <label className="initial-form-label">Giriş Şifresi Tekrar</label>
                            <input aria-label="password input"
                                type="password"
                                className="initial-form-input"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                disabled={isLoading}
                                placeholder="Giriş şifresini tekrar girin"
                            />
                        </div>
                    </div>

                    {/* Section Divider */}
                    <div className="initial-section-divider">
                        <span className="initial-section-divider-text">Admin Şifresi</span>
                    </div>

                    {/* Admin Şifresi Bilgileri */}
                    <div className="initial-form-group">
                        <label className="initial-form-label">Admin Şifresi</label>
                        <input aria-label="password input"
                            type="password"
                            className="initial-form-input"
                            value={rolePassword}
                            onChange={(e) => setRolePassword(e.target.value)}
                            required
                            disabled={isLoading}
                            placeholder="Admin kullanıcısı için ayrı bir şifre oluştur"
                        />
                        <small className="initial-form-help-text">
                            Admin kullanıcısı için ayrı bir şifre oluşturun. Bu şifre, admin paneli girişi için kullanılacaktır.
                        </small>
                    </div>

                    <div className="initial-form-group">
                        <label className="initial-form-label">Admin Şifresi Tekrar</label>
                        <input aria-label="password input"
                            type="password"
                            className="initial-form-input"
                            value={confirmRolePassword}
                            onChange={(e) => setConfirmRolePassword(e.target.value)}
                            required
                            disabled={isLoading}
                            placeholder="Admin şifresini tekrar girin"
                        />
                    </div>

                    {/* Hizmet Sözleşmesi */}
                    <div className="initial-form-group">
                        <label className="initial-terms-checkbox">
                            <input
                                type="checkbox"
                                checked={termsAccepted}
                                onChange={(e) => setTermsAccepted(e.target.checked)}
                                required
                                disabled={isLoading}
                            />
                            <span className="initial-terms-text">
                                <a href="https://tservis.tr/distance-selling" target="_blank" rel="noopener noreferrer">Mesafeli Satış Sözleşmesi</a>, 
                                <a href="https://tservis.tr/delivery-return" target="_blank" rel="noopener noreferrer"> Teslimat ve İade Koşulları</a> ve 
                                <a href="https://tservis.tr/privacy" target="_blank" rel="noopener noreferrer"> Gizlilik Politikası</a>'nı okudum ve kabul ediyorum.
                            </span>
                        </label>
                    </div>

                    {error && (
                        <div className="initial-error-message">
                            {error}
                            </div>
                    )}

                    <button
                        type="submit"
                        className="initial-btn initial-btn-primary"
                        disabled={isLoading}
                    >
                                {isLoading ? (
                                    <>
                                <div className="initial-button-spinner"></div>
                                Kayıt oluşturuluyor...
                                    </>
                                ) : (
                            'Şirket ve Admin Oluştur'
                                )}
                            </button>
                        </form>

                {/* Info Card */}
                <div className="info-card">
                    <p>
                        Bu işlem şirketinizi, admin kullanıcınızı ve tüm veritabanı yapısını oluşturacaktır. 
                        İşlem tamamlandıktan sonra telefon numaranıza SMS ile doğrulama kodu gönderilecektir.
                    </p>
                    </div>
                </div>
        </div>
    );
}

export default InitialRegister;
